// =============================================================================
// AICallsModal — drilldown for the AI Calls KPI card on the tenant Dashboard.
//
// Shows three views of AI usage for the same window the Dashboard is on:
//   1. Daily calls trend (line chart, derived from the same usage_metrics
//      series the Dashboard already loads — no extra fetch needed)
//   2. Calls by feature (horizontal bar — e.g. cultural-guide / scan-document /
//      trip-chat / …) with error counts overlaid
//   3. All users with AI activity, in a ranked sortable table
//
// The user-table replaces the older "top-5 with combined-score" card. Two
// problems with the old design:
//   • The combined "total_activity" score added ai_calls and trips_touched —
//     two different units — and produced a number nobody could explain.
//   • The summary tile labelled "Active users" actually held the SUM of
//     ai_calls from the top 5 — not a user count at all.
// New design:
//   • Real "Active users" tile = COUNT of users with AI activity in window.
//   • Sortable table with explicit columns: Rank · User · AI calls · Trips
//     touched. Tabular-nums keeps digits aligned. Sort dropdown (default
//     "AI calls") so the modal's primary metric drives the order. Default
//     view is top 10; "Show all N" toggles to the full list (server caps
//     at 200 so the payload stays bounded).
//
// Reuses the recharts library that's already a dep. The modal is positioned
// at size='xl' (max-w-4xl) to give the bars and labels enough room.
// =============================================================================

import { useMemo, useState } from 'react';
import { Modal, Spinner } from '@/components/shared/UIComponents';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell,
} from 'recharts';
import { Sparkles, AlertCircle, Users as UsersIcon } from 'lucide-react';
import { formatCompact } from '@/utils/helpers';
import type { UsageMetric } from '@/types';
import type { AiByFeatureEntry, AiErrorStats, AiActiveUserEntry } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  windowLabel: string;
  metrics: UsageMetric[];          // current period, padded
  aiByFeature: AiByFeatureEntry[];
  aiErrors: AiErrorStats | null;
  /** Every user with at least one AI call in the window, server-sorted
   *  by ai_calls DESC (capped at 200). Powers the all-users table. */
  aiActiveUsers: AiActiveUserEntry[];
  loading?: boolean;
}

type UserSort = 'ai_calls' | 'trips_touched';

export function AiCallsModal({
  open, onClose, windowLabel, metrics, aiByFeature, aiErrors, aiActiveUsers, loading,
}: Props) {
  // Daily-calls series for the line chart.
  const dailySeries = metrics.map(m => ({
    date: m.date.slice(5),     // strip year for x-axis tick density
    calls: m.ai_requests || 0,
  }));

  const totalCalls   = aiByFeature.reduce((s, f) => s + f.calls, 0);
  const totalErrors  = aiByFeature.reduce((s, f) => s + f.errors, 0);
  const errorRate    = aiErrors?.error_rate ?? (totalCalls > 0 ? Math.round((totalErrors / totalCalls) * 1000) / 10 : 0);
  const featuresUsed = aiByFeature.length;

  // Real "active users" = distinct users with AI activity (not a sum of
  // their calls). aiActiveUsers comes pre-filtered server-side to users
  // with ai_calls > 0, so length IS the active-user count for the window.
  const activeUserCount = aiActiveUsers.length;

  // ── All-users table state ──
  // Default sort: ai_calls (matches the modal's primary metric). The
  // server already orders by ai_calls DESC so a quick early-return saves
  // a sort cycle on the common case.
  const [userSort, setUserSort] = useState<UserSort>('ai_calls');
  const [showAll, setShowAll] = useState(false);

  const sortedUsers = useMemo(() => {
    if (userSort === 'ai_calls') return aiActiveUsers;
    return [...aiActiveUsers].sort((a, b) => b.trips_touched - a.trips_touched || b.ai_calls - a.ai_calls);
  }, [aiActiveUsers, userSort]);

  const visibleUsers = showAll ? sortedUsers : sortedUsers.slice(0, 10);
  const maxCalls     = sortedUsers[0]?.ai_calls || 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title="AI Calls"
      subtitle={`Detailed breakdown · ${windowLabel}`}
    >
      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="md" /></div>
      ) : (
        <div className="space-y-6">
          {/* ── Summary strip ──────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCell
              icon={<Sparkles size={14} />}
              label="Total calls"
              value={formatCompact(totalCalls)}
              tooltip="Every successful AI request the tenant made in the window — all features combined."
            />
            <SummaryCell
              icon={<AlertCircle size={14} />}
              label="Errors"
              value={`${totalErrors} (${errorRate}%)`}
              tone={errorRate > 5 ? 'danger' : errorRate > 1 ? 'warn' : 'ok'}
              tooltip="AI requests that returned an error. Error rate is errors ÷ (errors + successes)."
            />
            <SummaryCell
              icon={<Sparkles size={14} />}
              label="Features used"
              value={featuresUsed}
              tooltip="Distinct AI feature categories that recorded at least one call in the window (shown in the chart below)."
            />
            <SummaryCell
              icon={<UsersIcon size={14} />}
              label="Active users"
              value={activeUserCount}
              tooltip="Count of distinct users in this tenant who made at least one AI call during the window. This is a user count — not a sum of calls."
            />
          </div>

          {/* ── Daily calls line chart ─────────────────────────────── */}
          <div className="glass-panel-solid p-4">
            <h4 className="text-xs font-semibold text-surface-700 mb-3">Daily calls</h4>
            {dailySeries.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-xs text-surface-500">
                No usage data yet
              </div>
            ) : (
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailySeries} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                    <CartesianGrid stroke="rgb(var(--surface-200))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'rgb(var(--surface-500))' }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10, fill: 'rgb(var(--surface-500))' }} allowDecimals={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: 'rgb(var(--surface-0))', border: '1px solid rgb(var(--surface-300))', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: 'rgb(var(--surface-900))' }}
                    />
                    <Line type="monotone" dataKey="calls" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 2, fill: '#8b5cf6' }} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ── By feature ─────────────────────────────────────────── */}
          <div className="glass-panel-solid p-4">
            <h4 className="text-xs font-semibold text-surface-700 mb-3">Calls by feature</h4>
            {aiByFeature.length === 0 ? (
              <div className="h-40 flex items-center justify-center text-xs text-surface-500">
                No AI traffic in this window
              </div>
            ) : (
              <div style={{ height: Math.max(160, aiByFeature.length * 26) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={aiByFeature}
                    layout="vertical"
                    margin={{ top: 0, right: 16, bottom: 0, left: 8 }}
                  >
                    <CartesianGrid stroke="rgb(var(--surface-200))" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: 'rgb(var(--surface-500))' }} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="feature"
                      width={150}
                      tick={{ fontSize: 11, fill: 'rgb(var(--surface-700))' }}
                    />
                    <Tooltip
                      contentStyle={{ background: 'rgb(var(--surface-0))', border: '1px solid rgb(var(--surface-300))', borderRadius: 8, fontSize: 12 }}
                      formatter={(v: number, name: string) => [v.toLocaleString(), name === 'calls' ? 'Calls' : 'Errors']}
                    />
                    <Bar dataKey="calls" radius={[0, 4, 4, 0]}>
                      {aiByFeature.map((row, i) => (
                        <Cell key={i} fill={row.errors > 0 ? '#a78bfa' : '#8b5cf6'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ── All users table ────────────────────────────────────── */}
          <div className="glass-panel-solid p-4">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <h4 className="text-xs font-semibold text-surface-700">
                  Users with AI activity
                  <span className="ml-2 text-surface-500 font-normal">
                    ({activeUserCount} total)
                  </span>
                </h4>
                <p className="text-[11px] text-surface-500 mt-0.5">
                  One row per user. Ranked by the column you choose below.
                </p>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-surface-600">
                <span className="font-semibold uppercase tracking-wider text-[10px]">Sort</span>
                <select
                  value={userSort}
                  onChange={(e) => setUserSort(e.target.value as UserSort)}
                  className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-white text-surface-900"
                >
                  <option value="ai_calls">AI calls</option>
                  <option value="trips_touched">Trips touched</option>
                </select>
              </label>
            </div>

            {sortedUsers.length === 0 ? (
              <div className="h-32 flex items-center justify-center text-xs text-surface-500">
                No AI activity from any user in this window
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm tabular-nums">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-surface-500 border-b border-surface-200">
                        <th className="text-right py-2 pr-2 w-10">#</th>
                        <th className="text-left py-2 pr-3">User</th>
                        <th
                          className="text-right py-2 px-2 w-28"
                          title="Successful AI requests this user made during the window. One request = one count, regardless of which feature."
                        >
                          AI calls
                        </th>
                        <th
                          className="text-right py-2 pl-2 w-28"
                          title="Trips this user created or modified in the window. A trip touched five times still counts as one."
                        >
                          Trips touched
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleUsers.map((u, i) => {
                        const initial = (u.full_name?.[0] || u.email?.[0] || '?').toUpperCase();
                        const display = u.full_name?.trim() || u.email || u.user_id.slice(0, 8);
                        const callPct = Math.round((u.ai_calls / maxCalls) * 100);
                        return (
                          <tr key={u.user_id} className="border-b border-surface-100 last:border-0 hover:bg-surface-50/50">
                            <td className="py-2 pr-2 text-right text-xs font-bold text-surface-400">{i + 1}</td>
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-full bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-500 text-[11px] font-bold shrink-0">
                                  {initial}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs font-medium text-surface-900 truncate">{display}</div>
                                  {u.email && u.full_name?.trim() && (
                                    <div className="text-[10px] text-surface-500 truncate">{u.email}</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <div className="inline-flex items-center gap-2 justify-end">
                                <div className="h-1.5 w-16 bg-surface-200 rounded-full overflow-hidden hidden sm:block">
                                  <div className="h-full rounded-full bg-violet-500" style={{ width: `${callPct}%` }} />
                                </div>
                                <span className="font-semibold text-surface-900 min-w-[2ch]">{u.ai_calls.toLocaleString()}</span>
                              </div>
                            </td>
                            <td className="py-2 pl-2 text-right text-surface-700">
                              {u.trips_touched.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {sortedUsers.length > 10 && (
                  <div className="mt-3 flex justify-center">
                    <button
                      onClick={() => setShowAll(s => !s)}
                      className="text-xs font-medium text-violet-600 hover:text-violet-700"
                    >
                      {showAll
                        ? `Show top 10`
                        : `Show all ${sortedUsers.length} users`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function SummaryCell({ icon, label, value, tone = 'neutral', tooltip }: {
  icon: React.ReactNode; label: string; value: string | number;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger';
  tooltip?: string;
}) {
  const cls =
    tone === 'danger' ? 'text-red-600 dark:text-red-400'
    : tone === 'warn' ? 'text-amber-600 dark:text-amber-400'
    : tone === 'ok'   ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-surface-900';
  return (
    <div
      className="bg-overlay/[0.04] border border-overlay/[0.06] rounded-xl p-3"
      title={tooltip}
    >
      <div className="flex items-center gap-1.5 text-surface-500 mb-1">
        {icon}<span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div className={`text-lg font-bold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
